<script setup>
import {
	DsGrid,
	DsTag,
	DsText,
	DsButtonGroup,
} from '@coloplast/design-system'

defineProps({
	title: {
		type: String,
		required: true
	},
	bodyText: {
		type: String,
		required: true
	},
	imageUrl: {
		type: String,
		required: true
	},
	imgAltText: {
		type: String,
		required: true
	},
	tagText: String,
	orderSwapped: Boolean,
	dark: Boolean
})
</script>

<!-- eslint-disable vue/no-v-text-v-html-on-component -->
<template>
	<DsGrid
		:columns="1"
		:columns-md="2"
		:gap="0"
		class="c-corporate-banner"
		:class="{ 'c-corporate-banner--dark': dark }"
		data-testid="corporate-banner-block-grid">
		<section
			class="c-corporate-banner__card"
			:class="{ 'c-corporate-banner__card--order-swapped': orderSwapped }"
			data-testid="corporate-banner-block-card">
			<div>
				<DsTag
					v-if="tagText"
					:text="tagText"
					class="c-corporate-banner__card__tag">
				</DsTag>

				<DsText
					html-element="h3"
					text-type="heading-3xl"
					text-style="medium">
					{{ title }}
				</DsText>
			</div>

			<div>
				<DsText
					html-element="div"
					text-type="body-md"
					text-style="regular"
					class="c-corporate-banner__card__text"
					v-html="bodyText">
				</DsText>

				<DsButtonGroup
					desktop-alignment="start"
					mobile-direction="column"
					class="c-corporate-banner__card__button-group">
					<slot></slot>
				</DsButtonGroup>
			</div>
		</section>

		<img
			:src="imageUrl"
			:alt="imgAltText"
			class="c-corporate-banner__image" />
	</DsGrid>
</template>

<style lang="scss">
@import 'multisite/styles/settings/settings';
@import 'multisite/styles/tools/tools';

.c-corporate-banner {
	// TEMPORARY FIX UNTIL DS-GRID SUPPORTS GAP PROP
	--ds-grid-gap: 0;

	--background-color: #{ds-color("bg", "accent")};
	--color: #{ds-color("fg", "default")};
	--text-color: #{ds-color("fg", "muted")};

	min-block-size: 420px;
	border-radius: ds-border-radius("md");
	background-color: var(--background-color);
	overflow: hidden;

	&__card {
		--card-padding: #{ds-space("10")};
		--card-margin: 0;

		display: flex;
		flex-direction: column;
		justify-content: space-between;

		padding: var(--card-padding);
		margin: var(--card-margin);
		color: var(--color);

		&__tag {
			color: var(--color);
			padding-block-end: ds-space("6");
		}

		&__text {
			color: var(--text-color);

			& > * {
				margin: 0;
			}
		}

		&__button-group {
			margin-block-start: ds-space("8");
		}

		&--order-swapped {
			order: 2;
		}

		@media(max-width: ds-breakpoint("md")) {
			--card-margin: 0 0 #{ds-space("8")} 0;

			padding: ds-space("8");

			&--order-swapped {
				--card-margin: #{ds-space("8")} 0 0 0;
			}
		}

		@media(min-width: ds-breakpoint("md")) and (max-width: ds-breakpoint("lg")) {
			&__button-group {
				flex-direction: column;
				align-items: stretch;

				a {
					inline-size: 100%;
					justify-content: center;
				}
			}
		}
	}

	&__image {
		inline-size: 100%;
		block-size: 100%;
		object-fit: cover;
	}

	&--dark {
		--background-color: #{ds-color("bg", "dark")};
		--color: #{ds-color("fg", "light")};
		--text-color: #{ds-color("fg", "on-color-muted")};
	}
}
</style>
